import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useToast } from '../contexts/ToastContext';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import VisitaAsistenciaEditor, {
  etiquetaAsistencia,
  VisitaAsistenciaResumenNota,
} from './VisitaAsistenciaEditor';
import ClienteCombobox from './ClienteCombobox';
import './Calendario.css';

function eventColorClass(event) {
  const seed = `${event?.id || ''}-${event?.title || ''}`;
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) hash = (hash * 31 + seed.charCodeAt(index)) | 0;
  return `calendar-event--palette-${Math.abs(hash) % 6}`;
}

function renderCalendarEvent(info) {
  const title = String(info.event.title || '').toUpperCase();
  return (
    <div className="calendar-event-content">
      <span className="calendar-event-dot" aria-hidden="true" />
      {info.timeText && <span className="calendar-event-time">{info.timeText}</span>}
      <span className="calendar-event-name">{title}</span>
      {!info.event.allDay && <span className="calendar-event-clock" aria-hidden="true">◷</span>}
    </div>
  );
}

const Calendario = () => {
  const { showSuccess, showError, showConfirm } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPersonalUser = user?.rol === 'PERSONAL';
  const [eventos, setEventos] = useState([]);
  const [rangoVisible, setRangoVisible] = useState(null);
  const [filtrosTipo, setFiltrosTipo] = useState({
    visita: true,
    parte: true
  });
  const [eliminandoVisita, setEliminandoVisita] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [eventoSeleccionado, setEventoSeleccionado] = useState(null);
  const [mostrarFormularioVisita, setMostrarFormularioVisita] = useState(false);
  const calendarRef = useRef(null);
  const rangoEventosSolicitadoRef = useRef('');
  const rangoVisibleRef = useRef('');

  // Estados para el formulario de nueva visita
  const [nuevaVisita, setNuevaVisita] = useState({
    clienteId: '',
    expedienteId: '',
    fecha: '',
    hora: '',
    tipo: 'Reunión',
    descripcion: '',
    direccion: '',
    contacto: '',
    recordatorio: true
  });

  const [clientes, setClientes] = useState([]);
  const [expedientes, setExpedientes] = useState([]);

  const normalizarRango = (rango) => {
    if (
      rango &&
      typeof rango === 'object' &&
      typeof rango.start === 'string' &&
      typeof rango.end === 'string'
    ) {
      return { start: rango.start, end: rango.end };
    }
    return null;
  };

  const fetchEventosCalendario = async (rango, { forzar = false } = {}) => {
    const nr = normalizarRango(rango);
    const claveRango = nr ? `${nr.start}|${nr.end}` : 'sin-rango';
    if (!forzar && rangoEventosSolicitadoRef.current === claveRango) return;
    rangoEventosSolicitadoRef.current = claveRango;
    const paramsCalendario = nr ? { start: nr.start, end: nr.end } : {};
    try {
      const responseEventos = await api.get('/visitas/calendario', { params: paramsCalendario });
      setEventos(Array.isArray(responseEventos.data) ? responseEventos.data : []);
    } catch (error) {
      if (rangoEventosSolicitadoRef.current === claveRango) {
        rangoEventosSolicitadoRef.current = '';
      }
      throw error;
    }
  };

  // Los eventos se cargan desde datesSet, cuando FullCalendar ya conoce su rango visible.
  // Así se evita solicitar primero todo el calendario y volver a solicitar el mismo rango al montar.
  useEffect(() => {
    let cancelado = false;
    void (async () => {
      setCargando(true);
      setError(null);
      try {
        await cargarCatalogos();
      } finally {
        if (!cancelado) setCargando(false);
      }
    })();
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al montar
  }, []);

  const cargarCatalogos = async () => {
    try {
      const responseClientes = await api.get('/clientes');
      setClientes(Array.isArray(responseClientes.data) ? responseClientes.data : []);

      const responseExpedientes = await api.get('/expedientes', {
        params: {
          page: 1,
          pageSize: 500,
          sort: 'createdAt',
          order: 'desc'
        }
      });
      const expedientesData = responseExpedientes.data?.expedientes || responseExpedientes.data;
      setExpedientes(Array.isArray(expedientesData) ? expedientesData : []);
    } catch (catalogError) {
      console.error('Error cargando catálogos del calendario:', catalogError);
    }
  };

  /**
   * Recarga eventos. Por defecto NO usa `cargando` a pantalla completa: si el calendario
   * está montado, poner `cargando=true` desmonta FullCalendar y al terminar vuelve a montar,
   * disparando `datesSet` otra vez → bucle infinito de peticiones a /visitas/calendario.
   */
  const cargarDatos = async (rango = null, opciones = {}) => {
    const { pantallaCompleta = false, forzar = true } = opciones;
    if (pantallaCompleta) {
      setCargando(true);
      setError(null);
    }
    try {
      await fetchEventosCalendario(rango, { forzar });
    } catch (error) {
      console.error('Error cargando datos del calendario:', error);
      setError('Error al cargar los datos del calendario');
    } finally {
      if (pantallaCompleta) setCargando(false);
    }
  };

  const eventosFiltrados = useMemo(() => {
    return (eventos || []).filter((e) => {
      const tipo = e?.extendedProps?.tipo;
      return filtrosTipo[tipo] !== false;
    });
  }, [eventos, filtrosTipo]);

  const resumen = useMemo(() => {
    const now = new Date();
    const inicioHoy = new Date(now);
    inicioHoy.setHours(0, 0, 0, 0);
    const finHoy = new Date(inicioHoy);
    finHoy.setDate(finHoy.getDate() + 1);
    const finSemana = new Date(inicioHoy);
    finSemana.setDate(finSemana.getDate() + 7);

    let hoy = 0;
    let proximaSemana = 0;
    let vencidos = 0;

    eventosFiltrados.forEach((evento) => {
      const start = new Date(evento.start);
      if (Number.isNaN(start.getTime())) return;
      if (start >= inicioHoy && start < finHoy) hoy += 1;
      if (start >= inicioHoy && start < finSemana) proximaSemana += 1;
      if (start < inicioHoy && evento?.extendedProps?.tipo === 'parte') {
        vencidos += 1;
      }
    });

    return {
      total: eventosFiltrados.length,
      hoy,
      proximaSemana,
      vencidos
    };
  }, [eventosFiltrados]);

  const proximosEventos = useMemo(() => {
    const now = new Date();
    return [...eventosFiltrados]
      .filter((evento) => {
        const start = new Date(evento.start);
        return !Number.isNaN(start.getTime()) && start >= now;
      })
      .sort((a, b) => new Date(a.start) - new Date(b.start))
      .slice(0, 8);
  }, [eventosFiltrados]);

  /** Expedientes solo del cliente elegido (para chips en el formulario). */
  const expedientesDelCliente = useMemo(() => {
    if (!nuevaVisita.clienteId) return [];
    return (expedientes || []).filter((exp) => String(exp?.cliente?.id) === String(nuevaVisita.clienteId));
  }, [expedientes, nuevaVisita.clienteId]);

  // Sin cliente → sin expediente. Cliente sin expedientes → expediente nulo. Cambio de cliente → invalidar expediente ajeno.
  useEffect(() => {
    if (!nuevaVisita.clienteId) {
      setNuevaVisita((prev) =>
        prev.expedienteId === '' ? prev : { ...prev, expedienteId: '' }
      );
      return;
    }
    const list = (expedientes || []).filter(
      (exp) => String(exp?.cliente?.id) === String(nuevaVisita.clienteId)
    );
    if (list.length === 0) {
      setNuevaVisita((prev) =>
        prev.expedienteId === '' ? prev : { ...prev, expedienteId: '' }
      );
      return;
    }
    setNuevaVisita((prev) => {
      const ok = list.some((exp) => String(exp.id) === String(prev.expedienteId));
      if (ok) return prev;
      if (prev.expedienteId === '') return prev;
      return { ...prev, expedienteId: '' };
    });
  }, [nuevaVisita.clienteId, expedientes]);

  const handleEventClick = (info) => {
    const e = info.event;
    setEventoSeleccionado({
      title: e.title,
      start: e.start,
      extendedProps: { ...(e.extendedProps || {}) },
    });
    setMostrarModal(true);
  };

  const handleDateClick = (info) => {
    setNuevaVisita(prev => ({
      ...prev,
      fecha: info.dateStr
    }));
    setMostrarFormularioVisita(true);
  };

  const handleSubmitVisita = async (e) => {
    e.preventDefault();
    try {
      await api.post('/visitas', nuevaVisita);
      setMostrarFormularioVisita(false);
      setNuevaVisita({
        clienteId: '',
        expedienteId: '',
        fecha: '',
        hora: '',
        tipo: 'Reunión',
        descripcion: '',
        direccion: '',
        contacto: '',
        recordatorio: true
      });
      cargarDatos(); // Recargar eventos
    } catch (error) {
      console.error('Error creando visita:', error);
      alert('Error al crear la visita');
    }
  };

  const handleEliminarVisita = async () => {
    const visitaId = eventoSeleccionado?.extendedProps?.visitaId;
    if (!visitaId) return;
    if (
      !window.confirm(
        '¿Eliminar esta visita? Esta acción no se puede deshacer.'
      )
    ) {
      return;
    }
    setEliminandoVisita(true);
    try {
      await api.delete(`/visitas/${visitaId}`);
      setMostrarModal(false);
      setEventoSeleccionado(null);
      await cargarDatos(normalizarRango(rangoVisible));
    } catch (err) {
      console.error('Error eliminando visita:', err);
      alert('No se pudo eliminar la visita. Intenta de nuevo.');
    } finally {
      setEliminandoVisita(false);
    }
  };

  const fechaYHoraLocalesDesdeDate = (d) => {
    const pad = (n) => String(n).padStart(2, '0');
    return {
      fecha: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
      hora: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
    };
  };

  const handleEventDrop = async (info) => {
    try {
      const evento = info.event;
      const { fecha: nuevaFecha, hora: nuevaHora } = fechaYHoraLocalesDesdeDate(evento.start);

      if (evento.extendedProps.tipo === 'visita') {
        await api.put(`/visitas/${evento.extendedProps.visitaId}`, {
          fecha: nuevaFecha,
          hora: nuevaHora
        });
      } else {
        info.revert();
        return;
      }
      await cargarDatos(rangoVisible);
    } catch (error) {
      console.error('Error actualizando evento:', error);
      info.revert(); // Revertir el cambio si hay error
      alert('Error al actualizar el evento');
    }
  };

  const handleDatesSet = (info) => {
    const nuevoRango = {
      start: info.startStr,
      end: info.endStr
    };
    const claveRango = `${nuevoRango.start}|${nuevoRango.end}`;
    if (rangoVisibleRef.current !== claveRango) {
      rangoVisibleRef.current = claveRango;
      setRangoVisible(nuevoRango);
    }
    cargarDatos(nuevoRango, { forzar: false });
  };

  if (cargando) {
    return (
      <div className="calendario-container">
        <div className="loading">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Cargando calendario...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="calendario-container">
        <div className="error">
          <i className="fas fa-exclamation-triangle"></i>
          <p>{error}</p>
          <button
            type="button"
            onClick={() => {
              cargarDatos(normalizarRango(rangoVisible), { pantallaCompleta: true });
            }}
            className="btn btn-primary"
          >
            <i className="fas fa-redo"></i> Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="calendario-container">
      <div className="calendario-header">
        <h2>
          <i className="fas fa-calendar-alt"></i>
          Calendario de Expedientes y Visitas
        </h2>
        <div className="calendario-actions">
          <button 
            onClick={() => setMostrarFormularioVisita(true)}
            className="btn btn-primary"
          >
            <i className="fas fa-plus"></i>
            Nueva Visita
          </button>
          <button 
            onClick={() => cargarDatos(normalizarRango(rangoVisible))}
            className="btn btn-secondary"
          >
            <i className="fas fa-sync"></i>
            Actualizar
          </button>
        </div>
      </div>

      <div className="calendario-leyenda">
        <div className="leyenda-item">
          <div className="leyenda-color" style={{backgroundColor: '#3b82f6'}}></div>
          <span>Visitas Programadas</span>
        </div>
        <div className="leyenda-item">
          <div className="leyenda-color" style={{backgroundColor: '#10b981'}}></div>
          <span>Visitas Confirmadas</span>
        </div>
        <div className="leyenda-item">
          <div className="leyenda-color" style={{backgroundColor: '#ef4444'}}></div>
          <span>Partes vencidas</span>
        </div>
        <div className="leyenda-item">
          <div className="leyenda-color" style={{backgroundColor: '#f59e0b'}}></div>
          <span>Partes próximas a vencer</span>
        </div>
        <div className="leyenda-item">
          <div className="leyenda-color" style={{backgroundColor: '#10b981'}}></div>
          <span>Partes con tiempo</span>
        </div>
      </div>

      <div className="calendario-toolbar-minimal">
        <div className="resumen-cards">
          <div className="resumen-card">
            <span className="label">Hoy</span>
            <strong>{resumen.hoy}</strong>
          </div>
          <div className="resumen-card">
            <span className="label">7 días</span>
            <strong>{resumen.proximaSemana}</strong>
          </div>
          <div className="resumen-card">
            <span className="label">Vencidos</span>
            <strong>{resumen.vencidos}</strong>
          </div>
          <div className="resumen-card">
            <span className="label">Total visible</span>
            <strong>{resumen.total}</strong>
          </div>
        </div>

        <div className="filtros-tipo">
          <label>
            <input
              type="checkbox"
              checked={filtrosTipo.visita}
              onChange={(e) => setFiltrosTipo((prev) => ({ ...prev, visita: e.target.checked }))}
            />
            Visitas
          </label>
          <label>
            <input
              type="checkbox"
              checked={filtrosTipo.parte}
              onChange={(e) => setFiltrosTipo((prev) => ({ ...prev, parte: e.target.checked }))}
            />
            Partes
          </label>
        </div>
      </div>

      <div className="calendario-wrapper">
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          hiddenDays={[0]}
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay'
          }}
          events={eventosFiltrados}
          eventClassNames={(info) => eventColorClass(info.event)}
          eventContent={renderCalendarEvent}
          eventClick={handleEventClick}
          dateClick={handleDateClick}
          eventDrop={handleEventDrop}
          datesSet={handleDatesSet}
          editable={true}
          selectable={true}
          dayMaxEvents={3}
          moreLinkClick="popover"
          height="auto"
          locale="es"
          buttonText={{
            today: 'Hoy',
            month: 'Mes',
            week: 'Semana',
            day: 'Día'
          }}
          eventTimeFormat={{
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          }}
        />
      </div>

      <div className="proximos-eventos">
        <h4>Próximos eventos</h4>
        {proximosEventos.length === 0 ? (
          <p className="empty">No hay eventos próximos en la vista actual.</p>
        ) : (
          <ul>
            {proximosEventos.map((evento) => {
              const fecha = new Date(evento.start);
              const tipo = evento?.extendedProps?.tipo || 'evento';
              return (
                <li key={evento.id}>
                  <span className={`tag tag-${tipo}`}>{tipo}</span>
                  <span className="titulo">{evento.title}</span>
                  <span className="fecha">
                    {fecha.toLocaleDateString('es-ES')} {evento.allDay ? '' : fecha.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Modal de detalles del evento */}
      {mostrarModal && eventoSeleccionado && (
        <div className="modal-overlay" onClick={() => setMostrarModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Detalles del Evento</h3>
              <button 
                className="btn-close"
                onClick={() => setMostrarModal(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="evento-detalle">
                <h4>{eventoSeleccionado.title}</h4>
                <p><strong>Tipo:</strong> {eventoSeleccionado.extendedProps.tipo}</p>
                <p><strong>Fecha:</strong> {eventoSeleccionado.start.toLocaleDateString('es-ES')}</p>
                {eventoSeleccionado.extendedProps.tipo === 'visita' && (
                  <>
                    <p><strong>Hora:</strong> {eventoSeleccionado.extendedProps.hora}</p>
                    <p><strong>Cliente:</strong> {eventoSeleccionado.extendedProps.cliente}</p>
                    <p><strong>Teléfono:</strong> {eventoSeleccionado.extendedProps.celular}</p>
                    {eventoSeleccionado.extendedProps.expediente && (
                      <p><strong>Expediente:</strong> {eventoSeleccionado.extendedProps.expediente}</p>
                    )}
                    {eventoSeleccionado.extendedProps.descripcion && (
                      <p><strong>Descripción:</strong> {eventoSeleccionado.extendedProps.descripcion}</p>
                    )}
                    {eventoSeleccionado.extendedProps.direccion && (
                      <p><strong>Dirección:</strong> {eventoSeleccionado.extendedProps.direccion}</p>
                    )}
                    {eventoSeleccionado.extendedProps.visitaId
                      ? (() => {
                          const ep = eventoSeleccionado.extendedProps;
                          const registrada =
                            ep.clienteAsistio === true || ep.clienteAsistio === false;
                          const lab = etiquetaAsistencia(ep.clienteAsistio);
                          const visitaMin = {
                            id: ep.visitaId,
                            fecha: eventoSeleccionado.start,
                            hora: ep.hora,
                            tipo: ep.tipoVisita || 'Cita',
                            clienteAsistio: ep.clienteAsistio,
                            comentarioAsistencia: ep.comentarioAsistencia,
                          };
                          if (registrada) {
                            return (
                              <VisitaAsistenciaResumenNota
                                visita={visitaMin}
                                lab={lab}
                                puedeEliminar={!isPersonalUser}
                                showConfirm={showConfirm}
                                showError={showError}
                                onActualizada={(data) => {
                                  setEventoSeleccionado((prev) =>
                                    prev
                                      ? {
                                          ...prev,
                                          extendedProps: {
                                            ...prev.extendedProps,
                                            clienteAsistio: data.clienteAsistio,
                                            comentarioAsistencia: data.comentarioAsistencia,
                                          },
                                        }
                                      : prev
                                  );
                                  showSuccess('Registro de asistencia eliminado');
                                  void cargarDatos(normalizarRango(rangoVisible));
                                }}
                              />
                            );
                          }
                          if (isPersonalUser) {
                            return (
                              <p style={{ marginTop: '0.75rem', color: '#6c757d', fontSize: '0.9rem' }}>
                                Asistencia: pendiente de registro por el equipo.
                              </p>
                            );
                          }
                          return (
                            <VisitaAsistenciaEditor
                              visitaId={ep.visitaId}
                              initialClienteAsistio={ep.clienteAsistio}
                              initialComentarioAsistencia={ep.comentarioAsistencia}
                              subtitulo="Al guardar (asistió o no), el registro queda fijado; bórralo aquí o desde el expediente si necesitas cambiarlo."
                              compact
                              onGuardado={(data) => {
                                setEventoSeleccionado((prev) =>
                                  prev
                                    ? {
                                        ...prev,
                                        extendedProps: {
                                          ...prev.extendedProps,
                                          clienteAsistio: data.clienteAsistio,
                                          comentarioAsistencia: data.comentarioAsistencia,
                                        },
                                      }
                                    : prev
                                );
                                showSuccess('Asistencia guardada');
                                void cargarDatos(normalizarRango(rangoVisible));
                              }}
                            />
                          );
                        })()
                      : null}
                  </>
                )}
                {eventoSeleccionado.extendedProps.tipo === 'parte' && (
                  <>
                    <p><strong>Parte:</strong> {eventoSeleccionado.extendedProps.descripcion}</p>
                    <p><strong>Estado:</strong> {eventoSeleccionado.extendedProps.estado}</p>
                    <p><strong>Cliente:</strong> {eventoSeleccionado.extendedProps.cliente}</p>
                    <p><strong>Expediente:</strong> {eventoSeleccionado.extendedProps.numeroExpediente}</p>
                    {eventoSeleccionado.extendedProps.asignadoA?.nombre && (
                      <p><strong>Asignado a:</strong> {eventoSeleccionado.extendedProps.asignadoA.nombre}</p>
                    )}
                    {eventoSeleccionado.extendedProps.expedienteId && (
                      <button
                        type="button"
                        className="btn btn-archivo-parte"
                        onClick={() => {
                          const ep = eventoSeleccionado.extendedProps;
                          const parte = encodeURIComponent(
                            (ep.descripcion || '').trim().replace(/\s+/g, '_')
                          );
                          setMostrarModal(false);
                          navigate(`/expedientes/${ep.expedienteId}?tab=archivos&parte=${parte}`);
                        }}
                      >
                        <i className="fas fa-folder-open" /> Ir al archivo
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="modal-footer calendario-modal-footer">
              {eventoSeleccionado.extendedProps.tipo === 'visita' &&
                eventoSeleccionado.extendedProps.visitaId && (
                  <button
                    type="button"
                    className="btn btn-danger"
                    disabled={eliminandoVisita}
                    onClick={handleEliminarVisita}
                  >
                    {eliminandoVisita ? (
                      <>
                        <i className="fas fa-spinner fa-spin" /> Eliminando…
                      </>
                    ) : (
                      <>
                        <i className="fas fa-trash-alt" /> Eliminar visita
                      </>
                    )}
                  </button>
                )}
              <button
                className="btn btn-secondary"
                onClick={() => setMostrarModal(false)}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Formulario de nueva visita */}
      {mostrarFormularioVisita && (
        <div className="modal-overlay" onClick={() => setMostrarFormularioVisita(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Nueva Visita</h3>
              <button 
                className="btn-close"
                onClick={() => setMostrarFormularioVisita(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form onSubmit={handleSubmitVisita} className="modal-body calendario-form-nueva-visita">
              <div className="form-group calendario-nueva-visita-cliente">
                <label>Cliente *</label>
                <ClienteCombobox
                  value={nuevaVisita.clienteId}
                  clientesLista={clientes}
                  allowCreate={false}
                  required
                  onChange={(e) =>
                    setNuevaVisita((prev) => ({ ...prev, clienteId: e.target.value }))
                  }
                />
              </div>

              <div className="form-group calendario-nueva-visita-expedientes">
                <label>Expediente</label>
                <p className="calendario-expedientes-hint">
                  Con expediente asociado la visita aparece en el calendario del caso. Si el cliente no tiene expedientes, queda sin expediente.
                </p>
                {!nuevaVisita.clienteId ? (
                  <p className="calendario-expedientes-placeholder">Seleccione un cliente para elegir expediente.</p>
                ) : (
                  <div className="expediente-chip-row" role="group" aria-label="Expediente">
                    <button
                      type="button"
                      className={`expediente-chip${nuevaVisita.expedienteId === '' ? ' expediente-chip--active' : ''}`}
                      onClick={() =>
                        setNuevaVisita((prev) => ({ ...prev, expedienteId: '' }))
                      }
                    >
                      Sin expediente
                    </button>
                    {expedientesDelCliente.map((exp) => (
                      <button
                        key={exp.id}
                        type="button"
                        className={`expediente-chip${
                          String(nuevaVisita.expedienteId) === String(exp.id) ? ' expediente-chip--active' : ''
                        }`}
                        onClick={() =>
                          setNuevaVisita((prev) => ({
                            ...prev,
                            expedienteId: String(exp.id),
                            clienteId: exp?.cliente?.id != null ? String(exp.cliente.id) : prev.clienteId,
                          }))
                        }
                      >
                        <span className="expediente-chip-num">{exp.numero}</span>
                        {exp?.tipo?.nombre ? (
                          <span className="expediente-chip-meta">{exp.tipo.nombre}</span>
                        ) : null}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Fecha *</label>
                  <input
                    type="date"
                    value={nuevaVisita.fecha}
                    onChange={(e) => setNuevaVisita({...nuevaVisita, fecha: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Hora *</label>
                  <input
                    type="time"
                    value={nuevaVisita.hora}
                    onChange={(e) => setNuevaVisita({...nuevaVisita, hora: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Tipo de Visita *</label>
                  <select
                    value={nuevaVisita.tipo}
                    onChange={(e) => setNuevaVisita({...nuevaVisita, tipo: e.target.value})}
                    required
                  >
                    <option value="Reunión">Reunión</option>
                    <option value="Inspección">Inspección</option>
                    <option value="Entrega">Entrega</option>
                    <option value="Seguimiento">Seguimiento</option>
                    <option value="Otro">Otro</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Contacto</label>
                  <input
                    type="text"
                    value={nuevaVisita.contacto}
                    onChange={(e) => setNuevaVisita({...nuevaVisita, contacto: e.target.value})}
                    placeholder="Teléfono o email adicional"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Descripción</label>
                <textarea
                  value={nuevaVisita.descripcion}
                  onChange={(e) => setNuevaVisita({...nuevaVisita, descripcion: e.target.value})}
                  placeholder="Detalles de la visita..."
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Dirección</label>
                <input
                  type="text"
                  value={nuevaVisita.direccion}
                  onChange={(e) => setNuevaVisita({...nuevaVisita, direccion: e.target.value})}
                  placeholder="Dirección de la visita"
                />
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={nuevaVisita.recordatorio}
                    onChange={(e) => setNuevaVisita({...nuevaVisita, recordatorio: e.target.checked})}
                  />
                  Enviar recordatorio
                </label>
              </div>

              <div className="modal-footer">
                <button 
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setMostrarFormularioVisita(false)}
                >
                  Cancelar
                </button>
                <button 
                  type="submit"
                  className="btn btn-primary"
                >
                  <i className="fas fa-save"></i>
                  Crear Visita
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendario;
